import { useContext, useState, useEffect, useCallback } from "react";
import { Box, Fade, IconButton, Tooltip, Typography } from "@mui/material";
import { Pause, Play, Save, X } from "lucide-react";
import { TimeRecorderContext } from "../context/TimeRecorderContext";
import useIsMobile from "../hooks/useIsMobile";
import useDraggablePosition from "../common/useDraggablePosition";
import { ReactComponent as EllipseIcon } from "icons/Menu/Ellipse.svg";
import { ReactComponent as TodoListIcon } from "icons/Menu/TodoList.svg";
import TextField from "components/common/TextField";
import { useGetTaskBlock, useUpdateBlockContent } from "services/tasks";
import { TimeEntry } from "components/common/TimeTracking";
import LinkRouter from "components/navigation/LinkRouter";
import { useTheme } from "@mui/material/styles";

const formatElapsedTime = (ms: number): string => {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(
      seconds,
    ).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const TimerActionIcon = ({
  icon: Icon,
  onClick,
  active,
  title,
  color,
  hoverBgColor,
  disabled,
}: {
  icon: typeof Pause;
  onClick: () => void;
  active?: boolean;
  title: string;
  color?: string;
  hoverBgColor?: string;
  disabled?: boolean;
}) => {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton
          onClick={onClick}
          disabled={disabled}
          sx={{
            display: "flex",
            width: "40px",
            height: "40px",
            padding: "4px 9px",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            borderRadius: "100px",
            border: "1px solid",
            borderColor: "border",
            "&:hover": { backgroundColor: hoverBgColor || "gray.3" },
            "&:disabled": { opacity: 0.4, cursor: "not-allowed" },
            ...(active
              ? { backgroundColor: "gray.3" }
              : { background: "none" }),
          }}
        >
          <Icon className="icon" color={color} size={20} />
        </IconButton>
      </span>
    </Tooltip>
  );
};

const FloatingTimerModule = ({
  position = "top-right",
}: {
  position?: "bottom-right" | "bottom-left" | "top-right" | "top-left";
}) => {
  const theme = useTheme();
  const {
    session,
    pauseRecording,
    resumeRecording,
    cancelRecording,
    updateDescription,
    saveRecording,
  } = useContext(TimeRecorderContext);
  const isMobile = useIsMobile();
  const { positionOffset, handleMouseDown, handleMouseMove, handleMouseUp } =
    useDraggablePosition(position, isMobile);
  const [localDescription, setLocalDescription] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const { request: getBlockRequest } = useGetTaskBlock();
  const { request: updateBlockRequest } = useUpdateBlockContent(() => {
    setIsSaving(false);
    cancelRecording();
    window.dispatchEvent(
      new CustomEvent("timeRecordingSaved", {
        detail: { taskBlockRef: session?.taskBlockRef },
      }),
    );
  });

  useEffect(() => {
    if (session?.description !== undefined) {
      setLocalDescription(session.description);
    }
  }, [session?.description]);

  const handleDescriptionChange = (value: string) => {
    setLocalDescription(value);
    updateDescription(value);
  };

  const canSave = localDescription.trim().length > 0;

  const handleSave = useCallback(async () => {
    if (!session || isSaving || !canSave) return;
    setIsSaving(true);

    const blockResponse = await getBlockRequest({
      taskBlockRef: session.taskBlockRef,
    });
    const existingContent = blockResponse?.block?.content
      ? JSON.parse(blockResponse.block.content)
      : {};
    const existingEntries: TimeEntry[] = existingContent.timeEntries || [];

    saveRecording((entry) => {
      const updatedContent = {
        ...existingContent,
        timeEntries: [...existingEntries, entry],
      };
      updateBlockRequest({
        taskBlockRef: session.taskBlockRef,
        content: JSON.stringify(updatedContent),
        contentType: "json",
        textContent: "",
        attachments: [],
      });
    });
  }, [
    session,
    saveRecording,
    updateBlockRequest,
    getBlockRequest,
    isSaving,
    canSave,
  ]);

  const handleCancel = () => {
    cancelRecording();
  };

  const isVisible = session !== null;
  const isRecording = session?.state === "recording";
  const isPaused = session?.state === "paused";

  return (
    <Fade in={isVisible} timeout={300}>
      <div
        style={{
          position: "fixed",
          zIndex: 10000,
          left: positionOffset.left,
          right: positionOffset.right,
          top: positionOffset.top,
          bottom: positionOffset.bottom,
          display: isVisible ? "block" : "none",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={(e) => handleMouseDown(e)}
        onTouchMove={(e) => handleMouseMove(e)}
        onTouchEnd={handleMouseUp}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            backgroundColor: "#fff",
            borderRadius: "12px",
            padding: "16px 24px",
            width: isMobile ? "90vw" : "300px",
            boxShadow:
              "0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08)",
            animation: "floatingTimerModuleFadeIn 0.3s ease-out forwards",
            border: `1px solid ${theme.palette.border}`,
          }}
        >
          <Box display="flex" flexDirection="column" width="100%">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom="4px"
            >
              <Typography
                variant="bodySmall"
                display="flex"
                flexDirection="row"
                gap="4px"
                alignItems="center"
                sx={{
                  "svg circle": {
                    fill: isRecording
                      ? theme.palette.secondary.dark
                      : isPaused
                        ? "#F59E0B"
                        : theme.palette.gray[6],
                    animation: isRecording ? "pulse 1.5s infinite" : "none",
                  },
                }}
              >
                <EllipseIcon height={6} width={6} />
                <Typography variant="bodyHeavy" component="span">
                  {formatElapsedTime(session?.elapsedMs || 0)}
                </Typography>
                {isRecording ? "Recording" : isPaused ? "Paused" : "Timer"}
              </Typography>
              <IconButton onClick={handleCancel} size="small">
                <X size={16} color={theme.palette.text.secondary} />
              </IconButton>
            </Box>

            <Box
              display="flex"
              alignItems="center"
              gap="6px"
              marginBottom="8px"
            >
              <TodoListIcon
                style={{ width: "16px", height: "16px", flexShrink: 0 }}
              />
              <LinkRouter
                to={`/tasks/${encodeURIComponent(session?.taskRef || "")}`}
                useColorScheme={true}
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "220px",
                  fontSize: "14px",
                }}
              >
                {session?.taskTitle || "Task"}
              </LinkRouter>
            </Box>

            <Box display="flex" gap="8px" alignItems="center">
              <TextField
                fullWidth
                placeholder="What are you working on?"
                value={localDescription}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                sx={{
                  flex: 1,
                  div: { minHeight: "40px" },
                  input: {
                    marginTop: "0",
                    paddingLeft: "12px",
                    paddingRight: "8px",
                    fontSize: "14px",
                  },
                }}
              />
              {isRecording ? (
                <TimerActionIcon
                  onClick={pauseRecording}
                  icon={Pause}
                  title="Pause"
                  color={theme.palette.text.secondary}
                />
              ) : (
                <TimerActionIcon
                  onClick={resumeRecording}
                  icon={Play}
                  title={isPaused ? "Resume" : "Start"}
                  color={theme.palette.text.secondary}
                />
              )}
              <TimerActionIcon
                onClick={handleSave}
                icon={Save}
                title={canSave ? "Save" : "Enter description to save"}
                color={
                  canSave ? theme.palette.primary.main : theme.palette.gray[6]
                }
                hoverBgColor={canSave ? theme.palette.primary.light : undefined}
                disabled={!canSave || isSaving}
              />
            </Box>
          </Box>
        </Box>
        <style>
          {`
            @keyframes floatingTimerModuleFadeIn {
              from {
                opacity: 0;
                transform: translateY(20px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes pulse {
              0%, 100% {
                opacity: 1;
              }
              50% {
                opacity: 0.5;
              }
            }
          `}
        </style>
      </div>
    </Fade>
  );
};

export default () => {
  return <FloatingTimerModule position="top-right" />;
};
